import { useCallback, useState } from 'react';
import { readPersistedState, writePersistedState } from 'utils/persistedStateStorage';

/**
 * Drop-in replacement for `useState` whose value is mirrored to storage under
 * `storageKey`, so it survives a remount within the same browser session
 * (e.g. a provider keyed by a plan/entity id that resets on navigation).
 * Pass `null` for `storageKey` to opt out of persistence for that render
 * (e.g. while an id the key depends on is not yet known) — it then behaves
 * like a plain `useState`.
 */
export function usePersistedState<T>(storageKey: string | null, initialValue: T) {
    const [value, setValue] = useState<T>(() => {
        if (!storageKey) return initialValue;
        const stored = readPersistedState<T>(storageKey);
        return stored ?? initialValue;
    });

    const setPersistedValue = useCallback(
        (update: T | ((prev: T) => T)) => {
            setValue((prev) => {
                const next = typeof update === 'function' ? (update as (prev: T) => T)(prev) : update;
                if (storageKey) writePersistedState(storageKey, next);
                return next;
            });
        },
        [storageKey],
    );

    return [value, setPersistedValue] as const;
}
